import { z } from 'zod';
import {
    CardPointeFundingOutcome,
    type CardPointeFundingRecord,
    type CardPointeFundingRequestDetails,
    type CardPointeFundingRequestQuery,
    type CardPointeFundingResponseBody,
    type CardPointeFundingResponseDetails,
    type CardPointeFundingScalar,
    type CardPointeFundingSummary,
    type CardPointeFundingTransaction,
    type CardPointeFundingTransactionSummary,
} from '../../../types/ipg/cardpointe/cardpointe-funding.types';

export const CardPointeFundingScalarSchema: z.ZodType<CardPointeFundingScalar> = z.union([
    z.string(),
    z.number(),
    z.null(),
]);

export const CardPointeFundingRecordSchema: z.ZodType<CardPointeFundingRecord> = z.record(
    z.string(),
    z.unknown()
);

export const CardPointeFundingRequestQuerySchema: z.ZodType<CardPointeFundingRequestQuery> =
    z.object({
        merchid: z.string().min(1),
        date: z.string().regex(/^(?:\d{4}|\d{8})$/),
        page: z.string().regex(/^\d+$/).optional(),
        limit: z.string().regex(/^\d+$/).optional(),
    });

export const CardPointeFundingRequestDetailsSchema: z.ZodType<CardPointeFundingRequestDetails> =
    z.object({
        method: z.literal('GET'),
        path: z.literal('/cardconnect/rest/funding'),
        query: CardPointeFundingRequestQuerySchema,
    });

export const CardPointeFundingTransactionSchema: z.ZodType<CardPointeFundingTransaction> = z.object(
    {
        fundingtxnid: CardPointeFundingScalarSchema.optional(),
        retref: z.string().min(1).optional(),
        authcode: z.string().min(1).optional(),
        authdate: CardPointeFundingScalarSchema.optional(),
        respcode: z.string().min(1).optional(),
        batchid: CardPointeFundingScalarSchema.optional(),
        cardproc: z.string().min(1).optional(),
        interchangeunitfee: z.string().optional(),
        interchangepercentfee: z.string().optional(),
        date: CardPointeFundingScalarSchema.optional(),
        cardnumber: z.string().min(1).optional(),
        cardtype: z.string().min(1).optional(),
        cardbrand: z.string().min(1).optional(),
        amount: z.string().min(1).optional(),
        currency: z.string().min(1).optional(),
        type: z.string().min(1).optional(),
        status: z.string().min(1).optional(),
        fundingid: CardPointeFundingScalarSchema.optional(),
        invoicenumber: z.string().optional(),
        sourcetransactionid: z.string().optional(),
        terminalnumber: z.string().optional(),
        achreturncode: z.string().optional(),
        parentretref: z.string().optional(),
    }
);

export const CardPointeFundingResponseBodySchema: z.ZodType<CardPointeFundingResponseBody> =
    z.object({
        fundings: z.array(CardPointeFundingRecordSchema).optional(),
        txns: z.array(CardPointeFundingTransactionSchema).optional(),
        adjustments: z.array(CardPointeFundingRecordSchema).optional(),
        chargebacks: z.array(CardPointeFundingRecordSchema).optional(),
        pageNumber: CardPointeFundingScalarSchema.optional(),
        perPageSize: CardPointeFundingScalarSchema.optional(),
        currentPageSize: CardPointeFundingScalarSchema.optional(),
        fundingdate: CardPointeFundingScalarSchema.optional(),
        merchid: z.string().min(1).optional(),
        datechanged: CardPointeFundingScalarSchema.optional(),
        fundingmasterid: CardPointeFundingScalarSchema.optional(),
        errormsg: z.string().min(1).optional(),
    });

export const CardPointeFundingTransactionSummarySchema: z.ZodType<CardPointeFundingTransactionSummary> =
    z.object({
        gateway_txn_id: z.string().min(1),
        amount: z.string().min(1).optional(),
        currency: z.string().min(1).optional(),
        transaction_type: z.string().min(1).optional(),
        settlement_status: z.string().min(1).optional(),
        authorization_code: z.string().min(1).optional(),
        batch_id: z.string().min(1).optional(),
        funding_id: z.string().min(1).optional(),
        funding_transaction_id: z.string().min(1).optional(),
    });

export const CardPointeFundingSummarySchema: z.ZodType<CardPointeFundingSummary> = z.object({
    merchant_id: z.string().min(1),
    funding_date: z.string().min(1).optional(),
    funding_master_id: z.string().min(1).optional(),
    transaction_count: z.number().int().nonnegative(),
    transactions: z.array(CardPointeFundingTransactionSummarySchema),
    matched_transaction: CardPointeFundingTransactionSummarySchema.optional(),
});

export const CardPointeFundingResponseDetailsSchema: z.ZodType<CardPointeFundingResponseDetails> =
    z.discriminatedUnion('outcome', [
        z.object({
            outcome: z.literal(CardPointeFundingOutcome.AVAILABLE),
            httpStatus: z.literal(200),
            body: CardPointeFundingResponseBodySchema,
            summary: CardPointeFundingSummarySchema,
        }),
        z.object({
            outcome: z.literal(CardPointeFundingOutcome.UNAVAILABLE),
            httpStatus: z.number().int().positive(),
            body: z.unknown(),
            message: z.string().min(1),
        }),
        z.object({
            outcome: z.literal(CardPointeFundingOutcome.MALFORMED_RESPONSE),
            httpStatus: z.number().int().positive(),
            body: z.unknown(),
            message: z.string().min(1),
        }),
    ]);
